import { killEvent } from '@medplum/react'
import { formatDate } from '@seen/core/dates'
import { referenceDisplay } from '@seen/core/display'
import { featureFlags } from '@seen/core/featureFlags'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import type { Order } from '@seen/core/models/orders/Order'
import { Participant } from '@seen/core/models/patients/participant'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import {
  Button,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  cn,
  IconStandingOrder,
} from '@seen/ui'
import {
  IconCalendarEvent,
  IconCopy,
  IconFile,
  IconFiles,
  IconLink,
  IconMessage,
  IconRepeat,
} from '@tabler/icons-react'
import pluralize from 'pluralize'
import { type JSX, type ReactNode, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ActivityComment } from '#src/components/activity/ActivityComment.tsx'
import { ContextMenuMedplumLinkItem } from '#src/components/ContextMenuMedplumLinkItem.tsx'
import { ContextMenuOpenInNewTab } from '#src/components/ContextMenuOpenInNewTab.tsx'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { HighlightedText } from '#src/components/core/HighlightedText.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { SearchMatchSnippet } from '#src/components/core/SearchMatchSnippet.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { IconDue } from '#src/components/icons/IconDue.tsx'
import { IconNewTask } from '#src/components/icons/IconNewTask.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { ContextMenuQuestLinkItem } from '#src/components/MenuQuestLinkItem.tsx'
import { OrderDetailDisplayInline } from '#src/components/order/OrderDetailDisplay.tsx'
import { OrderLabelRow } from '#src/components/order/OrderLabelRow.tsx'
import { OrderNextTaskDisplay } from '#src/components/order/OrderNextTaskDisplay.tsx'
import {
  OrderPriorityCombobox,
  OrderPriorityIconButton,
} from '#src/components/order/OrderPriorityCombobox.tsx'
import { OrderStatusCombobox } from '#src/components/order/OrderStatusCombobox.tsx'
import { useOrderDocumentReferences } from '#src/components/order/useOrderDocumentReferences.ts'
import { useOrdersPageStore } from '#src/components/order/useOrdersPageStore.ts'
import { ParticipantTaskRowBadge } from '#src/components/ParticipantTaskRowBadge.tsx'
import { RowBadge } from '#src/components/TaskRowBadge.tsx'
import { TaskRowCheckbox } from '#src/components/TaskRowCheckbox.tsx'
import {
  countStemOccurrencesMulti,
  type MatchInfo,
} from '#src/hooks/useClientSideSearch.ts'
import { useElementSize } from '#src/hooks/useElementSize.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useLinkOrigin } from '#src/hooks/useLinkOrigin.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'
import { useTaskComposerStore } from '#src/hooks/useTaskComposerStore.ts'
import { getOrderTitle } from '#src/utils/getOrderTitle.ts'
import { toast } from '#src/utils/toast.ts'

export interface OrderListRowProps {
  order: Order
  hideParticipant?: boolean
  hideCheckbox?: boolean
  className?: string
  checked?: boolean
  onCheckedChange?: (value: boolean, isShiftClick?: boolean) => void
  appointment?: { start?: string }
  isLastViewed?: boolean
  prefix?: ReactNode
  comment?: CommentCommunication
  /* Search match data — when present the title is highlighted and a
   * snippet from the best matching hidden field renders below it. */
  match?: MatchInfo
  extractedFields?: Record<string, string>
  display?: {
    showLabels?: boolean
    showNextAppointment?: boolean
    showNextTask?: boolean
    showLastNote?: boolean
    showPerformer?: boolean
    showRequester?: boolean
    showDueDate?: boolean
    showDocuments?: boolean
  }
}

export function OrderListRow({
  order,
  hideParticipant,
  hideCheckbox = true,
  className,
  checked,
  onCheckedChange,
  appointment,
  isLastViewed,
  prefix,
  comment: commentProp,
  match,
  extractedFields,
  display,
}: OrderListRowProps): JSX.Element {
  const linkOrigin = useLinkOrigin()
  const { explicit: documentReferences } = useOrderDocumentReferences(order)
  const setLastViewedOrderId = useOrdersPageStore((s) => s.setLastViewedOrderId)
  const { data: participant } = useRead(Participant, order.participant)
  const { data: fetchedComment } = useSearchOne(
    CommentCommunication,
    commentProp ? undefined : CommentCommunication.byPartOf(order.reference)
  )
  const comment = commentProp ?? fetchedComment

  const setTaskComposerOpen = useTaskComposerStore((s) => s.setOpen)
  const setTaskComposerDefaultValues = useTaskComposerStore(
    (s) => s.setDefaultValues
  )

  const { data: tasks } = useSearch(
    StandardTask,
    StandardTask.activeByFocus(order.reference)
  )
  const sortedTasks = StandardTask.priorityOrderedTasks(tasks ?? [])
  const nextTask = sortedTasks?.[0]

  const [highlight, setHighlight] = useState<undefined | boolean>(undefined)

  useEffect(() => {
    if (isLastViewed) {
      setHighlight(true)
      const timeout = setTimeout(() => {
        setHighlight(false)
        setLastViewedOrderId(null)
      }, 1500)
      return () => {
        clearTimeout(timeout)
      }
    }
    return () => {}
  }, [isLastViewed, setLastViewedOrderId])

  const [containerRef, containerSize] = useElementSize<HTMLAnchorElement>()
  const [detailRef, detailSize] = useElementSize<HTMLDivElement>()
  const [detailMinSizeThresholdPassed, setDetailMinSizeThresholdPassed] =
    useState(false)
  if (
    detailSize.width &&
    detailSize.width < 200 &&
    !detailMinSizeThresholdPassed
  ) {
    setDetailMinSizeThresholdPassed(true)
  }

  const navigate = useNavigate()
  const { patientId } = useParams<{ patientId?: string }>()
  const participantScopedReorderEnabled = useFeatureFlag(
    featureFlags.orderReorderParticipantScoped
  )

  // Must stay absolute: the context menu renders a plain <a href>, so a
  // relative path would be resolved against the current URL by the browser.
  const orderHref = participant
    ? `${participant.relativeUrl}/orders/${order.id}`
    : `/orders/${order.id}`
  const reorderBaseUrl =
    participantScopedReorderEnabled && patientId
      ? `/Patient/${patientId}/orders`
      : '/orders'

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <Link
          ref={containerRef}
          data-testid="order-row"
          onClick={() => setLastViewedOrderId(order.id)}
          to={orderHref}
          className={cn(
            'group flex h-full flex-col justify-center px-pagex-default hover:bg-accent data-[state=open]:bg-accent lg:px-pagex-lg',
            checked && 'bg-accent',
            highlight === true && 'animate-comment-border-highlight-in',
            highlight === false && 'animate-comment-border-highlight-out',
            !hideCheckbox && 'pr-8 pl-0 lg:pr-8 lg:pl-0',
            className
          )}
        >
          <div className="flex h-full w-full items-center">
            {!hideCheckbox && (
              <TaskRowCheckbox value={checked} onChange={onCheckedChange} />
            )}
            {prefix}
            <div className="flex min-w-0 flex-1 flex-row items-center gap-2 py-0">
              {/* overflow-hidden is load-bearing: min-w-0 lets flexbox shrink
               * this group to zero width in a narrow list (split view, narrow
               * window), and without a clip its content keeps painting on top
               * of the shrink-0 chip group to its right. */}
              <div
                data-testid="order-row-content"
                className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden"
              >
                <div className="flex justify-center gap-2" onClick={killEvent}>
                  <OrderStatusCombobox order={order} />
                  <ResourceFieldForm
                    instance={order}
                    valueFn={(o) => o.priority}
                    patchFn={(o, value) => {
                      o.priority = value ?? undefined
                    }}
                    className="h-full"
                    render={({ field: { value, onChange, ...rest } }) => (
                      <OrderPriorityCombobox
                        onSelect={onChange}
                        selected={value ?? null}
                      >
                        <OrderPriorityIconButton
                          priority={value ?? null}
                          {...rest}
                        />
                      </OrderPriorityCombobox>
                    )}
                  />
                </div>
                <span
                  data-testid="order-row-identifier"
                  className="min-w-[6ch] shrink-0 text-sm whitespace-nowrap text-foreground-secondary"
                >
                  {order.seenIdentifier}
                </span>
                {order.participant && !hideParticipant && (
                  <div className="w-[120px]">
                    <ParticipantTaskRowBadge participant={order.participant} />
                  </div>
                )}
                {order.standing && (
                  <Tooltip>
                    <TooltipTrigger>
                      <IconStandingOrder />
                    </TooltipTrigger>
                    <TooltipContent>Standing order</TooltipContent>
                  </Tooltip>
                )}
                <div
                  ref={detailRef}
                  className="flex min-w-0 flex-1 items-center gap-2"
                >
                  {match ? (
                    <OrderSearchDetail
                      order={order}
                      match={match}
                      extractedFields={extractedFields}
                    />
                  ) : (
                    <OrderDetailDisplayInline order={order} />
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center justify-end gap-2">
                {display?.showNextTask !== false && !nextTask && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        className="opacity-0 group-hover:opacity-100"
                        variant="ghost"
                        size="sm"
                        aria-label={`Create new task for ${order.seenIdentifier}`}
                        onClick={(e) => {
                          killEvent(e)
                          setTaskComposerOpen(true)
                          setTaskComposerDefaultValues({
                            focus: order.reference,
                            participant: order.participant,
                          })
                        }}
                      >
                        <IconNewTask />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Create new task for {order.seenIdentifier}
                    </TooltipContent>
                  </Tooltip>
                )}
                {display?.showLabels !== false && order.labels.length > 0 && (
                  <OrderLabelRow
                    order={order}
                    onClick={(e) => e.preventDefault()}
                    maxLabelsToShow={detailMinSizeThresholdPassed ? 0 : 1}
                  />
                )}
                {display?.showNextTask !== false && nextTask && (
                  <OrderNextTaskDisplay
                    task={nextTask}
                    rowWidth={containerSize.width}
                  />
                )}
                {display?.showNextAppointment !== false &&
                  appointment?.start && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <RowBadge
                          icon={<IconCalendarEvent className="size-3.5" />}
                        >
                          {formatDate(appointment.start)}
                        </RowBadge>
                      </TooltipTrigger>
                      <TooltipContent>Next appointment date</TooltipContent>
                    </Tooltip>
                  )}
                {display?.showDocuments !== false &&
                  documentReferences.length > 0 && (
                    <Tooltip>
                      <TooltipTrigger>
                        {documentReferences.length > 1 ? (
                          <IconFiles className="size-3.5" />
                        ) : (
                          <IconFile className="size-3.5" />
                        )}
                      </TooltipTrigger>
                      <TooltipContent>
                        {documentReferences.length}{' '}
                        {pluralize('document', documentReferences.length)}
                      </TooltipContent>
                    </Tooltip>
                  )}
                {display?.showLastNote !== false && comment && (
                  <Tooltip>
                    <TooltipTrigger>
                      <IconMessage className="size-3.5" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <ActivityComment comment={comment} />
                    </TooltipContent>
                  </Tooltip>
                )}
                {display?.showDueDate !== false && order.dueDate && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                    }}
                  >
                    <ResourceFieldForm
                      instance={order}
                      valueFn={(o) => o.dueDate}
                      patchFn={(o, v) => {
                        o.dueDate = v ?? undefined
                      }}
                      render={({ field }) => (
                        <DatePicker {...field} asChild>
                          <RowBadge
                            icon={
                              <IconDue
                                dateString={field.value ?? ''}
                                isCompleted={
                                  order.status === 'coordinated' ||
                                  order.status === 'effectuated' ||
                                  order.status === 'completed'
                                }
                                className="size-3.5"
                              />
                            }
                          >
                            {field.value ? formatDate(field.value) : ''}
                          </RowBadge>
                        </DatePicker>
                      )}
                    />
                  </span>
                )}
                {display?.showRequester !== false && order.requester && (
                  <Tooltip>
                    <TooltipTrigger>
                      <Avatar profile={order.requester} size="icon" />
                    </TooltipTrigger>
                    <TooltipContent>
                      Requester: {referenceDisplay(order.requester)}
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        </Link>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem
          className="gap-2"
          onSelect={() => {
            void navigator.clipboard.writeText(
              `${linkOrigin()}/orders/${order.id}`
            )
            toast.info('Order URL copied to clipboard')
          }}
        >
          <IconLink className="size-4 stroke-muted-foreground" />
          Copy URL
        </ContextMenuItem>
        {order.seenIdentifier && (
          <ContextMenuItem
            className="gap-2"
            onSelect={() => {
              if (order.seenIdentifier) {
                void navigator.clipboard.writeText(order.seenIdentifier)
                toast.info('Order identifier copied to clipboard')
              }
            }}
          >
            <IconCopy className="size-4 stroke-muted-foreground" />
            Copy ID
          </ContextMenuItem>
        )}
        <ContextMenuItem
          className="gap-2"
          onSelect={() => {
            void navigate(`${reorderBaseUrl}/new?reorderId=${order.id}`)
          }}
        >
          <IconRepeat className="size-4 stroke-muted-foreground" />
          Re-order
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuOpenInNewTab href={orderHref} />
        <ContextMenuMedplumLinkItem to={order} />
        <ContextMenuQuestLinkItem to={order} />
      </ContextMenuContent>
    </ContextMenu>
  )
}

/* Replaces `OrderDetailDisplayInline` when a search match is present.
 * Shows highlighted category + title on one line, with a snippet from the
 * best matching hidden field below. Candidate priority: details → reasons →
 * codeDisplays (line-item test names) → participant (patient name, global page only).
 * Non-search path keeps `OrderDetailDisplayInline` exactly as-is. */
function OrderSearchDetail({
  order,
  match,
  extractedFields,
}: {
  order: Order
  match: MatchInfo
  extractedFields: Record<string, string> | undefined
}): JSX.Element {
  const fields = extractedFields ?? {
    details: order.orderDetailsText ?? '',
    reasons: order.reasonTexts.join(' '),
    codeDisplays: order.displayCodes
      .map((c) => c.display)
      .filter(Boolean)
      .join(' '),
  }

  const { perField } = countStemOccurrencesMulti(fields, match.terms)
  const snippetField =
    (perField.details ?? 0) > 0
      ? 'details'
      : (perField.reasons ?? 0) > 0
        ? 'reasons'
        : (perField.codeDisplays ?? 0) > 0
          ? 'codeDisplays'
          : (perField.participant ?? 0) > 0
            ? 'participant'
            : null

  const fullLabel = `${order.categoryCodeDisplay}: ${order.aiTitle ?? getOrderTitle(order)}`

  return (
    <span className="flex w-full flex-col gap-0.5 overflow-hidden text-sm">
      <span className="truncate">
        <HighlightedText text={fullLabel} terms={match.terms} />
      </span>
      {snippetField && (
        <SearchMatchSnippet
          hiddenFieldText={fields[snippetField] ?? ''}
          stemmedTerms={match.terms}
          hiddenFieldMatches={perField[snippetField] ?? 0}
        />
      )}
    </span>
  )
}
